/**
 * Wizard Utilities
 *
 * Helper functions for wizard operations:
 * - Nested object expansion (Canvas hierarchies)
 * - Auto-mapping field logic
 * - Field type coercion
 * - Validation helpers
 *
 * Based on wizard-framework utility functions
 */
import type { MetadataField, MetadataObject } from './metadataTransform';

/**
 * Expand nested objects recursively
 * Used for Canvas and other hierarchical systems
 *
 * Based on wizard-framework expandNestedObjects()
 */
export const expandNestedObjects = (
  objects: MetadataObject[],
  depth: number = 0,
  maxDepth: number = 5
): MetadataObject[] => {
  if (depth >= maxDepth) return objects;

  const expanded: MetadataObject[] = [];

  objects.forEach((obj) => {
    expanded.push(obj);

    // Check if object has nested children
    const nestedFields = obj.fields?.filter((f) => f.type === 'reference' || f.type === 'object');

    if (nestedFields && nestedFields.length > 0) {
      nestedFields.forEach((field) => {
        // Create nested object representation
        const nestedObj: MetadataObject = {
          name: `${obj.name}.${field.name}`,
          label: `${obj.label} > ${field.label}`,
          fields: field.referenceTo || [],
          isNested: true,
          parentObject: obj.name,
          nestingLevel: depth + 1,
        };

        // Recursively expand if needed
        const furtherExpanded = expandNestedObjects([nestedObj], depth + 1, maxDepth);
        expanded.push(...furtherExpanded);
      });
    }
  });

  return expanded;
};

/**
 * Auto-map fields between source and destination
 * Matches by name, label, or common patterns
 *
 * Based on wizard-framework autoMapFields()
 */
export interface FieldMapping {
  sourceField: string;
  destinationField: string;
  transform?: string;
  confidence?: 'high' | 'medium' | 'low';
}

export const autoMapFields = (
  sourceFields: MetadataField[],
  destinationFields: MetadataField[],
  options: {
    caseSensitive?: boolean;
    matchByType?: boolean;
    includePartialMatches?: boolean;
  } = {}
): FieldMapping[] => {
  const { caseSensitive = false, matchByType = true, includePartialMatches = false } = options;

  const mappings: FieldMapping[] = [];
  const normalize = (str: string) => (caseSensitive ? str : str.toLowerCase());

  sourceFields.forEach((srcField) => {
    let bestMatch: MetadataField | null = null;
    let confidence: 'high' | 'medium' | 'low' = 'low';

    // Priority 1: Exact name match
    bestMatch =
      destinationFields.find((df) => normalize(df.name) === normalize(srcField.name)) || null;

    if (bestMatch) {
      confidence = 'high';
    } else {
      // Priority 2: Exact label match
      bestMatch =
        destinationFields.find((df) => normalize(df.label) === normalize(srcField.label)) || null;

      if (bestMatch) {
        confidence = 'high';
      } else if (includePartialMatches) {
        // Priority 3: Partial match (contains)
        bestMatch =
          destinationFields.find(
            (df) =>
              normalize(df.name).includes(normalize(srcField.name)) ||
              normalize(srcField.name).includes(normalize(df.name))
          ) || null;

        if (bestMatch) {
          confidence = 'medium';
        }
      }
    }

    // Type check if enabled
    if (bestMatch && matchByType && srcField.type !== bestMatch.type) {
      // Lower confidence if types don't match
      confidence = confidence === 'high' ? 'medium' : 'low';
    }

    if (bestMatch) {
      mappings.push({
        sourceField: srcField.name,
        destinationField: bestMatch.name,
        confidence,
      });
    }
  });

  return mappings;
};

/**
 * Coerce field value to target type
 * Handles common type conversions
 *
 * Based on wizard-framework coerceFieldType()
 */
export const coerceFieldValue = (value: any, targetType: string): any => {
  if (value === null || value === undefined) return value;

  switch (targetType.toLowerCase()) {
    case 'string':
    case 'text':
      return String(value);

    case 'number':
    case 'integer':
    case 'int':
      return Number(value);

    case 'boolean':
    case 'bool':
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true' || value === '1';
      }
      return Boolean(value);

    case 'date':
    case 'datetime':
      if (typeof value === 'string' || typeof value === 'number') {
        return new Date(value).toISOString();
      }
      return value;

    case 'array':
      if (Array.isArray(value)) return value;
      return [value];

    case 'object':
    case 'json':
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }
      return value;

    default:
      return value;
  }
};

/**
 * Validate field mapping configuration
 * Checks for common issues
 */
export const validateFieldMapping = (
  mapping: FieldMapping,
  sourceFields: MetadataField[],
  destinationFields: MetadataField[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check if source field exists
  const sourceField = sourceFields.find((f) => f.name === mapping.sourceField);
  if (!sourceField) {
    errors.push(`Source field "${mapping.sourceField}" not found`);
  }

  // Check if destination field exists
  const destField = destinationFields.find((f) => f.name === mapping.destinationField);
  if (!destField) {
    errors.push(`Destination field "${mapping.destinationField}" not found`);
  }

  // Check type compatibility
  if (sourceField && destField && sourceField.type !== destField.type) {
    // Only warn, not error (coercion may be possible)
    errors.push(`Type mismatch: ${sourceField.type} → ${destField.type} (coercion may be needed)`);
  }

  // Check required fields
  if (destField?.required && !mapping.sourceField) {
    errors.push(`Required destination field "${destField.label}" has no source mapping`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get field type compatibility score
 * Returns 0-100 score indicating how compatible two types are
 */
export const getTypeCompatibilityScore = (sourceType: string, destType: string): number => {
  if (sourceType === destType) return 100;

  // Define compatibility matrix
  const compatibilityMap: Record<string, string[]> = {
    string: ['text', 'varchar', 'char', 'email', 'url', 'phone'],
    text: ['string', 'varchar', 'char'],
    number: ['integer', 'int', 'decimal', 'float', 'double'],
    integer: ['number', 'int', 'decimal'],
    boolean: ['bool', 'bit'],
    date: ['datetime', 'timestamp'],
    datetime: ['date', 'timestamp'],
  };

  const sourceCompat = compatibilityMap[sourceType.toLowerCase()] || [];
  if (sourceCompat.includes(destType.toLowerCase())) return 75;

  // Check if coercion is possible
  if (sourceType === 'string') return 50; // Strings can be coerced to most types

  return 0; // Incompatible
};

/**
 * Sort objects alphabetically (default transformer)
 * Based on wizard-framework defaultMetadataTransform()
 */
export const sortObjectsAlphabetically = (objects: MetadataObject[]): MetadataObject[] => {
  return [...objects].sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Filter objects by search term
 * Searches both name and label
 */
export const filterObjects = (objects: MetadataObject[], searchTerm: string): MetadataObject[] => {
  if (!searchTerm) return objects;

  const term = searchTerm.toLowerCase();
  return objects.filter(
    (obj) => obj.name.toLowerCase().includes(term) || obj.label.toLowerCase().includes(term)
  );
};
